import { StoreApi, UseBoundStore, create } from 'zustand';

import { useRef } from 'react';
import type {
  ContextItemFileType,
  ContextItemType,
  ContextItems,
} from 'src/@types/ai';
import type { Doc } from 'src/@types/docs';

export interface AIInputState {
  inputValue: string;
  doSearch: boolean;
  think: boolean;
  generateImage: boolean;
  contextFiles: ContextItems[];
  selectedContext: string;
  currentDocument: Doc | null;
  audioAnswer: boolean;
  isLoading: boolean;
  error: string | null;
  setCurrentDocument: (document: Doc | null) => void;
  setSelectedContext: (context: string) => void;
  setInputValue: (value: string) => void;
  toggleSearch: () => void;
  toggleThink: () => void;
  toggleGenerateImage: () => void;
  toggleContextFiles: (
    type: ContextItemType,
    contextFile: ContextItemFileType,
  ) => void;
  toggleAudioAnswer: () => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  hasContextFile: (
    type: ContextItemType,
    contextFile: ContextItemFileType,
  ) => boolean;
}

export const useAIInputStore = () => {
  const storeRef = useRef<UseBoundStore<StoreApi<AIInputState>>>();
  if (!storeRef.current) {
    storeRef.current = createStore();
  }
  return storeRef.current;
};

export type AIInputStateControl = ReturnType<typeof useAIInputStore>;

export const createStore = () =>
  create<AIInputState>((set, get) => ({
    inputValue: '',
    doSearch: false,
    think: false,
    generateImage: false,
    contextFiles: [],
    selectedContext: '',
    currentDocument: null,
    audioAnswer: false,
    isLoading: false,
    error: null,
    setCurrentDocument: (document: Doc | null) =>
      set({ currentDocument: document }),
    setSelectedContext: (context: string) => set({ selectedContext: context }),
    setInputValue: (value: string) => set({ inputValue: value }),
    toggleSearch: () =>
      set((state) => ({
        doSearch: !state.doSearch,
        generateImage: false,
      })),
    toggleThink: () =>
      set((state) => ({
        think: !state.think,
        generateImage: false,
      })),
    toggleGenerateImage: () =>
      set((state) => ({
        generateImage: !state.generateImage,
        think: false,
        doSearch: false,
      })),
    toggleContextFiles: (type, contextFile) => {
      const { contextFiles, hasContextFile } = get();
      const newContextFiles = hasContextFile(type, contextFile)
        ? [
            ...contextFiles.filter(
              (file) =>
                contextFile._id !== (file[file.type] as { _id: string })._id,
            ),
          ]
        : [...contextFiles, { type, [type]: contextFile }];
      set({ contextFiles: newContextFiles });
    },
    toggleAudioAnswer: () =>
      set((state) => ({ audioAnswer: !state.audioAnswer })),
    setIsLoading: (loading: boolean) => set({ isLoading: loading }),
    setError: (error: string | null) => set({ error }),
    reset: () => set({ inputValue: '', isLoading: false, error: null }),
    hasContextFile: (type, contextFile) => {
      const { contextFiles } = get();
      return contextFiles.some(
        (file) =>
          type === file.type &&
          contextFile._id === (file[file.type] as { _id: string })._id,
      );
    },
  }));
